import React, { useEffect, useState } from "react";
import axios from "axios";
import { Heart, Sparkles, TrendingUp, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * CompassionScore — unified ecosystem-impact widget.
 *
 * Variants:
 *  - "sat"  (default): Midnight/Royal-violet/Gold palette for SAT pages.
 *  - "herz": Weinrot/Gold/Creme palette for Herzbewegung pages.
 */
export default function CompassionScore({ variant = "sat" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("sat_token");
    if (!token) { setLoading(false); return; }
    axios
      .get(`${API}/api/compassion/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10" data-testid="compassion-loading">
        <Loader2 className="animate-spin text-[#D4AF37]" size={18} />
      </div>
    );
  }
  if (!data) return null;

  const isHerz = variant === "herz";
  const bgPanel = isHerz ? "rgba(212,175,55,0.04)" : "rgba(164,140,255,0.04)";
  const borderColor = isHerz ? "#D4AF3740" : "#D4AF3740";
  const textBase = isHerz ? "#F7EEDD" : "#ffffff";
  const muted = isHerz ? "#F7EEDDaa" : "#ffffffaa";
  const subtle = isHerz ? "#F7EEDD55" : "#ffffff55";

  const lvl = data.level || {};
  const nxt = data.next_level;
  const progressPct = Math.round((data.progress_to_next || 0) * 100);

  const Pill = ({ label, count, points, weight, color }) => (
    <div
      className="flex-1 min-w-[140px] rounded-xl p-4 border"
      style={{ borderColor: `${color}40`, background: `${color}08` }}
      data-testid={`compassion-pill-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: muted }}>
        {label}
      </div>
      <div className="serif text-3xl font-light mt-2" style={{ color }}>
        {points}
      </div>
      <div className="text-[11px] mt-1" style={{ color: subtle }}>
        {count} × {weight} Punkte
      </div>
    </div>
  );

  return (
    <div
      className="rounded-2xl border p-6 md:p-8 mb-10"
      style={{ borderColor, background: bgPanel, color: textBase }}
      data-testid="compassion-score-widget"
    >
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-2">
            <Sparkles size={12} /> Compassion Score
          </div>
          <h3 className="serif text-2xl md:text-3xl font-light leading-tight">
            Dein Wirkungs-Index im Ökosystem.
          </h3>
          <p className="text-sm mt-2 italic" style={{ color: muted }}>
            SAT-Empfehlungen + Herzenstaten + Achievements — eine einzige Zahl.
          </p>
        </div>
        <div className="text-right">
          <div
            className="serif font-light leading-none"
            style={{ fontSize: "3.5rem", color: lvl.color || "#D4AF37" }}
            data-testid="compassion-total"
          >
            {data.total}
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] mt-1" style={{ color: muted }}>
            Punkte
          </div>
        </div>
      </div>

      {/* Level + progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
          <div className="flex items-center gap-2">
            <Heart size={14} style={{ color: lvl.color }} className="fill-current" />
            <span
              className="serif text-lg"
              style={{ color: lvl.color }}
              data-testid="compassion-level"
            >
              {lvl.label}
            </span>
          </div>
          {nxt ? (
            <div className="text-xs" style={{ color: muted }}>
              Noch <b style={{ color: lvl.color }}>{data.points_to_next}</b> bis{" "}
              <span className="italic">{nxt.label}</span>
            </div>
          ) : (
            <div className="text-xs" style={{ color: muted }}>
              <TrendingUp size={11} className="inline mr-1" /> Höchste Stufe erreicht
            </div>
          )}
        </div>
        <div
          className="h-2 rounded-full overflow-hidden"
          style={{ background: isHerz ? "rgba(247,238,221,0.08)" : "rgba(255,255,255,0.06)" }}
        >
          <div
            className="h-full transition-all duration-700 rounded-full"
            style={{
              width: `${progressPct}%`,
              background: `linear-gradient(90deg, ${lvl.color || "#D4AF37"} 0%, ${
                nxt?.color || "#D4AF37"
              } 100%)`,
            }}
            data-testid="compassion-progress-bar"
          />
        </div>
      </div>

      {/* Breakdown pills */}
      <div className="flex flex-wrap gap-3" data-testid="compassion-breakdown">
        <Pill
          label="Empfehlungen"
          count={data.breakdown.referrals.count}
          points={data.breakdown.referrals.points}
          weight={data.breakdown.referrals.weight}
          color="#a48cff"
        />
        <Pill
          label="Herzenstaten"
          count={data.breakdown.heart_acts.count}
          points={data.breakdown.heart_acts.points}
          weight={data.breakdown.heart_acts.weight}
          color="#E8B4A2"
        />
        <Pill
          label="Achievements"
          count={data.breakdown.achievements.count}
          points={data.breakdown.achievements.points}
          weight={data.breakdown.achievements.weight}
          color="#D4AF37"
        />
      </div>
    </div>
  );
}
